import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Collapse from "@mui/material/Collapse"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Divider from "@mui/material/Divider"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row from "../../../common/components/Row"
import { isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import { getEmailEditorHTML } from "../email-campaigns/edit-email-mixin"
import { SETTINGS_TABS, createSettingsTabURL } from "../settings"
import { useDialogContainer } from "../utils"
import DialogHeader from "./DialogHeader"
import EmailWYSWIGEditor, {
  useEmailWYSWIGEditorState,
} from "./EmailWYSWIGEditor"
import RenderedEmailBody from "./RenderedEmailBody"
import SelectCampaignAutoComplete, {
  useSelectCampaignAutoCompleteState,
} from "./SelectCampaignAutoComplete"
import UpdateCreateEmailCampaignDialog, {
  useUpdateCreateEmailCampaignDialogState,
} from "./UpdateCreateEmailCampaignDialog"
import {
  CONCIERGE_EMAIL_SIGNATURE_API_URL,
  EMAIL_SIGNATURE_API_URL,
  buildSignaturePreviewHtml,
  parseIncludeSignature,
} from "./emailSignatureUtils"

export default function SelectEmailCampaignDialog({
  open,
  onClose,
  bodyPropName,
  primaryButton = { text: "", isLoading: false, onClick: () => {} },
  campaignsAPIURL,
  onCampaignCreated = () => {},
  selectedCampaignID,
  editable = false,
}) {
  const {
    availableCampaigns,
    setAvailableCampaigns,
    selectedCampaign,
    setSelectedCampaign,
    campaignInputValue,
    setCampaignInputValue,
    campaignsLoaded,
    campaignsLoading,
  } = useSelectCampaignAutoCompleteState(open, campaignsAPIURL)
  const {
    createCampaignDialogOpen,
    handleCreateCampaignDialogClosed,
    setCreateCampaignDialogOpen,
  } = useUpdateCreateEmailCampaignDialogState()
  const { editorReady, setEditorReady, editorRef, setEditorBody } =
    useEmailWYSWIGEditorState(open && editable)
  const Container = useDialogContainer()
  const profile = useSelector(selectProfile)
  const concierge = isConcierge(profile)
  const navigate = useNavigate()
  const axios = useAuthorizedAxiosClient()
  const [signaturePreviewHtml, setSignaturePreviewHtml] = useState("")
  const [signatureLoaded, setSignatureLoaded] = useState(false)
  const emailSettingsURL = createSettingsTabURL(SETTINGS_TABS.configureEmail)

  const templateIncludesSignature = selectedCampaign
    ? parseIncludeSignature(selectedCampaign.include_signature)
    : false
  const signatureRequiredButMissing =
    templateIncludesSignature && signatureLoaded && !signaturePreviewHtml

  useEffect(() => {
    if (!open) {
      setSignaturePreviewHtml("")
      setSignatureLoaded(false)
      return
    }
    const signatureURL = concierge
      ? CONCIERGE_EMAIL_SIGNATURE_API_URL
      : EMAIL_SIGNATURE_API_URL
    axios
      .get(signatureURL)
      .then(({ data }) =>
        setSignaturePreviewHtml(buildSignaturePreviewHtml(data))
      )
      .catch(() => setSignaturePreviewHtml(""))
      .finally(() => setSignatureLoaded(true))
  }, [axios, concierge, open])

  useEffect(() => {
    if (!editable || !editorReady || !selectedCampaign || !signatureLoaded) {
      return
    }
    if (templateIncludesSignature && !signaturePreviewHtml) {
      if (concierge) {
        navigate(emailSettingsURL)
        setSelectedCampaign(null)
        setCampaignInputValue("")
      }
      return
    }
    setEditorBody(
      selectedCampaign[bodyPropName] || "",
      templateIncludesSignature,
      signaturePreviewHtml
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editable,
    editorReady,
    signatureLoaded,
    selectedCampaign?.id,
    bodyPropName,
    signaturePreviewHtml,
    templateIncludesSignature,
  ])

  function handleSoftClose() {
    onClose()
  }

  function handleCreateCampaignLinkClicked() {
    setCreateCampaignDialogOpen(true)
    handleSoftClose()
  }

  function handlePrimaryButtonClicked() {
    if (!selectedCampaign || signatureRequiredButMissing) {
      return
    }

    if (editable) {
      primaryButton.onClick(selectedCampaign, getEmailEditorHTML(editorRef))
      return
    }

    primaryButton.onClick(selectedCampaign)
  }

  useEffect(
    () => {
      if (!selectedCampaignID || !campaignsLoaded) {
        return
      }

      const matchingCampaign = availableCampaigns.find(
        (campaign) => campaign.id === selectedCampaignID
      )
      if (matchingCampaign) {
        setCampaignInputValue(matchingCampaign.name)
        setSelectedCampaign(matchingCampaign)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCampaignID, campaignsLoaded]
  )

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      maxWidth="md"
      fullWidth
      keepMounted
    >
      <DialogHeader title="Select Email Template" onClose={handleSoftClose} />
      <ConditionalPurpleLinearProgress visible={campaignsLoading} />
      <DialogContent>
        <Column gap={2}>
          <SelectCampaignAutoComplete
            availableCampaigns={availableCampaigns}
            setAvailableCampaigns={setAvailableCampaigns}
            campaignInputValue={campaignInputValue}
            setCampaignInputValue={setCampaignInputValue}
            selectedCampaign={selectedCampaign}
            setSelectedCampaign={setSelectedCampaign}
            campaignsLoaded={campaignsLoaded}
            campaignsLoading={campaignsLoading}
            onCreateCampaignLinkClicked={handleCreateCampaignLinkClicked}
            onCampaignCreated={onCampaignCreated}
          />
          <Collapse in={!!selectedCampaign}>
            <Column sx={{ mt: 2, gap: 1 }}>
              <Row sx={{ alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  {editable ? "Edit email" : "Email Preview"}
                </Typography>
                {selectedCampaign?.include_signature ? (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={
                      concierge
                        ? "Your signature will be included"
                        : "Includes email signature"
                    }
                  />
                ) : null}
              </Row>
              {signatureRequiredButMissing ? (
                <Alert
                  severity="warning"
                  action={
                    concierge ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => navigate(emailSettingsURL)}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Set up signature
                      </Button>
                    ) : null
                  }
                >
                  {concierge
                    ? "This template includes a signature. Set up your email signature before you can send."
                    : "This template includes a signature. Add your email signature in Settings → Email before sending."}
                </Alert>
              ) : null}
              {editable && selectedCampaign ? (
                <EmailWYSWIGEditor
                  editorRef={editorRef}
                  editorReady={editorReady}
                  setEditorReady={setEditorReady}
                  includeSignature={templateIncludesSignature}
                  signaturePreviewHtml={signaturePreviewHtml}
                />
              ) : (
                <>
                  <RenderedEmailBody
                    renderedBody={
                      selectedCampaign ? selectedCampaign[bodyPropName] : ""
                    }
                  />
                  {templateIncludesSignature && signaturePreviewHtml ? (
                    <>
                      <Divider />
                      <Typography variant="caption" color="text.secondary">
                        Email signature preview
                      </Typography>
                      <RenderedEmailBody
                        renderedBody={signaturePreviewHtml}
                        sx={{ mt: 0 }}
                      />
                    </>
                  ) : null}
                </>
              )}
              {selectedCampaign?.include_signature && editable ? (
                <>
                  <Divider />
                  <Typography variant="caption" color="text.secondary">
                    Your signature is added automatically at the end when this
                    template includes one.
                  </Typography>
                </>
              ) : editable ? (
                <Typography variant="caption" color="text.secondary">
                  Edit the message below before sending. Changes apply to this
                  email only and do not update the saved template.
                </Typography>
              ) : null}
            </Column>
          </Collapse>
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleSoftClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={
            !selectedCampaign ||
            campaignsLoading ||
            (editable && !editorReady) ||
            signatureRequiredButMissing
          }
          loading={primaryButton.isLoading}
          onClick={handlePrimaryButtonClicked}
        >
          {primaryButton.text}
        </LoadingButton>
      </DialogActions>
      <UpdateCreateEmailCampaignDialog
        open={createCampaignDialogOpen}
        onClose={handleCreateCampaignDialogClosed(
          setAvailableCampaigns,
          onCampaignCreated
        )}
      />
    </Container>
  )
}

export function useBaseSelectEmailCampaign(open, apiURL) {
  const [availableCampaigns, setAvailableCampaigns] = useState([])
  const [campaignsLoaded, setCampaignsLoaded] = useState(false)
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      if (!open) {
        setCampaignsLoaded(false)
        setCampaignsLoading(false)
        return
      }

      let cancelled = false
      setCampaignsLoading(true)
      setCampaignsLoaded(false)

      axios
        .get(apiURL)
        .then(({ data }) => {
          if (!cancelled) {
            setAvailableCampaigns(data)
          }
        })
        .finally(() => {
          if (!cancelled) {
            setCampaignsLoaded(true)
            setCampaignsLoading(false)
          }
        })

      return () => {
        cancelled = true
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, apiURL]
  )
  return {
    availableCampaigns,
    setAvailableCampaigns,
    campaignsLoaded,
    campaignsLoading,
  }
}
